import { DealInformation } from '@luna-protocol/core/src/types';
import { OEMType as EOEMType } from '@luna-protocol/core/src/types.ts';
import type { SetStateAction } from 'react';
import { create } from 'zustand';

import type { ApplicationSummaryDetails } from './AppContext';
import type { ProgramProps } from './components/ProgramSelect/ProgramSelect';
import type { PostSerialResponse } from './queries/useGetSerialNumber';
import dealReducer from './reducers/dealReducer';
import type { DealerInfo, FlowTemplate, OrderSummary, UserDetails } from './types';

type ResolveableAction<T> = SetStateAction<T>;

const isBrowser = typeof window !== 'undefined';

const getParsedStorageValue = <T>(value: string | null): T | undefined => {
  if (value === null) {
    return undefined;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return undefined;
  }
};

const getSessionValue = <T>(key: string): T | undefined => {
  if (!isBrowser) {
    return undefined;
  }

  const storedValue = window.sessionStorage.getItem(key);

  return getParsedStorageValue<T>(storedValue);
};

const setSessionValue = <T>(key: string, value: T | undefined) => {
  if (!isBrowser) {
    return;
  }

  if (value === undefined) {
    window.sessionStorage.removeItem(key);
    return;
  }

  window.sessionStorage.setItem(key, JSON.stringify(value));
};

const getLocalValue = <T>(key: string): T | undefined => {
  if (!isBrowser) {
    return undefined;
  }

  const storedValue = window.localStorage.getItem(key);

  return getParsedStorageValue<T>(storedValue);
};

const setLocalValue = <T>(key: string, value: T | undefined) => {
  if (!isBrowser) {
    return;
  }

  if (value === undefined) {
    window.localStorage.removeItem(key);
    return;
  }

  window.localStorage.setItem(key, JSON.stringify(value));
};

const resolveStateAction = <T>(action: ResolveableAction<T>, current: T): T => {
  if (typeof action === 'function') {
    return (action as (prev: T) => T)(current);
  }

  return action;
};

export type AppStoreState = {
  userDetails?: UserDetails;
  prequalAmount?: string;
  deal?: Partial<DealInformation>;
  applicationSummaryDetails?: ApplicationSummaryDetails;
  floorplanDetails: Map<string, PostSerialResponse>;
  loanApplicationID: string;
  customerName?: string;
  selectedProgram?: ProgramProps;
  OEMType: EOEMType;
  serialNumbers?: OrderSummary;
  dealerFlowTemplate?: FlowTemplate;
  dealerInfo?: DealerInfo;
  setUserDetails: (value: ResolveableAction<UserDetails | undefined>) => void;
  setPrequalAmount: (value: ResolveableAction<string | undefined>) => void;
  setDeal: (value: Partial<DealInformation>) => void;
  setApplicationSummaryDetails: (value: ResolveableAction<ApplicationSummaryDetails | undefined>) => void;
  setFloorplanDetails: (value: ResolveableAction<Map<string, PostSerialResponse>>) => void;
  setLoanApplicationID: (value: ResolveableAction<string>) => void;
  setCustomerName: (value: ResolveableAction<string | undefined>) => void;
  setSelectedProgram: (value: ResolveableAction<ProgramProps | undefined>) => void;
  setOEMType: (value: ResolveableAction<EOEMType>) => void;
  setSerialNumbers: (value: OrderSummary | undefined) => void;
  setDealerFlowTemplate: (value: ResolveableAction<FlowTemplate | undefined>) => void;
  setDealerInfo: (value: ResolveableAction<DealerInfo | undefined>) => void;
};

export const useAppStore = create<AppStoreState>()(set => ({
  userDetails: getSessionValue<UserDetails>('userDetails'),
  prequalAmount: getSessionValue<string>('prequalAmount'),
  deal: undefined,
  applicationSummaryDetails: getSessionValue<ApplicationSummaryDetails>('applicationSummaryDetails'),
  floorplanDetails: new Map<string, PostSerialResponse>(),
  loanApplicationID: getSessionValue<string>('loanApplication_id') ?? '',
  customerName: '',
  selectedProgram: undefined,
  OEMType: getSessionValue<EOEMType>('OEMType') ?? EOEMType.Brunswick,
  serialNumbers: undefined,
  dealerFlowTemplate: getLocalValue<FlowTemplate>('flow_template'),
  dealerInfo: undefined,
  setUserDetails: action =>
    set(state => {
      const nextUserDetails = resolveStateAction(action, state.userDetails);
      setSessionValue('userDetails', nextUserDetails);

      return { userDetails: nextUserDetails };
    }),
  setPrequalAmount: action =>
    set(state => {
      const nextPrequalAmount = resolveStateAction(action, state.prequalAmount);
      setSessionValue('prequalAmount', nextPrequalAmount);

      return { prequalAmount: nextPrequalAmount };
    }),
  setDeal: dealOrPartialDeal =>
    set(state => {
      const nextDeal = dealReducer({ ...(state.deal ?? {}), ...dealOrPartialDeal }, state.serialNumbers);

      return { deal: nextDeal };
    }),
  setApplicationSummaryDetails: action =>
    set(state => {
      const nextApplicationSummaryDetails = resolveStateAction(action, state.applicationSummaryDetails);
      setSessionValue('applicationSummaryDetails', nextApplicationSummaryDetails);

      return { applicationSummaryDetails: nextApplicationSummaryDetails };
    }),
  setFloorplanDetails: action =>
    set(state => {
      const nextFloorplanDetails = resolveStateAction(action, state.floorplanDetails);

      return { floorplanDetails: nextFloorplanDetails };
    }),
  setLoanApplicationID: action =>
    set(state => {
      const nextLoanApplicationID = resolveStateAction(action, state.loanApplicationID);
      setSessionValue('loanApplication_id', nextLoanApplicationID);

      return { loanApplicationID: nextLoanApplicationID };
    }),
  setCustomerName: action =>
    set(state => {
      const nextCustomerName = resolveStateAction(action, state.customerName);

      return { customerName: nextCustomerName };
    }),
  setSelectedProgram: action =>
    set(state => {
      const nextSelectedProgram = resolveStateAction(action, state.selectedProgram);

      return { selectedProgram: nextSelectedProgram };
    }),
  setOEMType: action =>
    set(state => {
      const nextOEMType = resolveStateAction(action, state.OEMType);
      setSessionValue('OEMType', nextOEMType);

      return { OEMType: nextOEMType };
    }),
  setSerialNumbers: payload =>
    set(state => {
      const nextSerialNumbers = payload ? { ...(state.serialNumbers ?? {}), ...payload } : undefined;
      const nextDeal = state.deal ? dealReducer({ ...state.deal }, nextSerialNumbers) : state.deal;

      return {
        serialNumbers: nextSerialNumbers,
        deal: nextDeal,
      };
    }),
  setDealerFlowTemplate: action =>
    set(state => {
      const nextDealerFlowTemplate = resolveStateAction(action, state.dealerFlowTemplate);
      setLocalValue('flow_template', nextDealerFlowTemplate);

      return { dealerFlowTemplate: nextDealerFlowTemplate };
    }),
  setDealerInfo: action =>
    set(state => {
      const nextDealerInfo = resolveStateAction(action, state.dealerInfo);

      return { dealerInfo: nextDealerInfo };
    }),
}));

export type { ApplicationSummaryDetails } from './AppContext';
